import React, { useState, useEffect } from "react";
import { Property } from "@/types/propertyTypes";
import {
  TrendingUp,
  Calendar,
  LineChart,
  BarChart3,
  Download,
} from "lucide-react";

interface ValuationPoint {
  date: string;
  value: number;
  type: "historical" | "current" | "projected";
  source: "valuation" | "market" | "ai";
  confidence?: number;
}


interface ValuationTimelineProps {
  property: Property;
}

const ValuationTimeline: React.FC<ValuationTimelineProps> = ({ property }) => {
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [valuationHistory, setValuationHistory] = useState<ValuationPoint[]>([]);
  const [hasLimitedData, setHasLimitedData] = useState<boolean>(false);
  const currentValue = property.valuationDetails.openMarketValue ;
  const constructionYear = property.yearOfConstruction;
  const currentYear = new Date().getFullYear();

  // Currency detection based on property country
  const isUganda =
    !!(property as any)?.location?.region ||
    (property as any)?.location?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";
  
  useEffect(() => {
    // Calculate historical growth (assume 5% annual growth)
    const annualGrowthRate = 1.05;
    const history: ValuationPoint[] = [];
    
    // Determine available years from property data
    const availableYears: number[] = [];
    
    // Check if we have construction year data
    if (constructionYear) {
      availableYears.push(constructionYear);
    }
    
    // Add current year
    availableYears.push(currentYear);
    
    // Sort years
    availableYears.sort((a, b) => a - b);
    
    // Check if we have limited data (only 2025 and 2026 or similar)
    const isLimitedData = availableYears.length <= 2 && 
                         availableYears[availableYears.length - 1] - availableYears[0] <= 1;
    setHasLimitedData(isLimitedData);
    
    // Generate data for each available year
    availableYears.forEach((year, index) => {
      let yearValue;
      
      if (year === currentYear) {
        yearValue = currentValue;
      } else {
        // Calculate value based on growth rate
        const yearsDifference = currentYear - year;
        yearValue = currentValue / Math.pow(annualGrowthRate, yearsDifference);
      }
      
      history.push({
        date: year.toString(),
        value: Math.round(yearValue),
        type: year === currentYear ? "current" : "historical",
        source: "valuation",
        confidence: year === currentYear ? 95 : 85,
      });
    });
    
    // If we have at least 2 years of data, we can show meaningful trends
    if (availableYears.length >= 2) {
      // Add one future projection if we have historical data
      const nextYear = currentYear + 1;
      const projectedValue = currentValue * annualGrowthRate;
      
      history.push({
        date: nextYear.toString(),
        value: Math.round(projectedValue),
        type: "projected",
        source: "ai",
        confidence: 75,
      });
    } else {
      // If only one year of data, add current year and a projection
      history.push({
        date: currentYear.toString(),
        value: Math.round(currentValue),
        type: "current",
        source: "valuation",
        confidence: 95,
      });
      
      // Add future projection
      const nextYear = currentYear + 1;
      const projectedValue = currentValue * annualGrowthRate;
      
      history.push({
        date: nextYear.toString(),
        value: Math.round(projectedValue),
        type: "projected",
        source: "ai",
        confidence: 70,
      });
    }
    
    setValuationHistory(history);
    setSelectedYear(currentYear);
  }, [property, currentValue, constructionYear, currentYear]);

  const calculateAnnualGrowth = (
    start: number,
    end: number,
    years: number
  ): number => {
    if (years === 0 || start === 0) return 0;
    return (Math.pow(end / start, 1 / years) - 1) * 100;
  };

  const currentYearData = valuationHistory.find(
    (v) => v.date === selectedYear.toString()
  );
  const maxValue = valuationHistory.length > 0 ? Math.max(...valuationHistory.map((v) => v.value)) : 0;
  const minValue = valuationHistory.length > 0 ? Math.min(...valuationHistory.map((v) => v.value)) : 0;

  if (valuationHistory.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading valuation data...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
              <LineChart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Property Valuation Timeline
              </h3>
              <p className="text-gray-600 text-sm">
                {hasLimitedData 
                  ? "Current valuation with projections based on available data"
                  : "Historical trends and future projections"}
              </p>
            </div>
          </div>
          {hasLimitedData && (
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm font-medium">
              Limited Data Available
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-8">
          {/* Data Availability Notice */}
          {hasLimitedData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">
                    Limited Historical Data
                  </h4>
                  <p className="text-blue-700 text-sm">
                    Showing valuation for {valuationHistory.filter(v => v.type === "historical" || v.type === "current").length} year(s). 
                    For more accurate projections, additional historical data is recommended.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Visualization */}
          <div className="relative h-64">
            {/* Grid Lines */}
            <div className="absolute inset-0 flex">
              {[0, 25, 50, 75, 100].map((percent) => {
                const range = maxValue - minValue;
                const gridValue =
                  range > 0 ? minValue + range * (percent / 100) : minValue;
                return (
                  <div
                    key={percent}
                    className="flex-1 border-r border-gray-100 relative"
                  >
                    <div className="absolute -top-6 right-0 translate-x-1/2 text-xs text-gray-500">
                      {currency} {(gridValue / 1000000).toFixed(1)}M
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Timeline Points */}
            <div className="absolute inset-0 flex items-end">
              {valuationHistory.map((point, index) => {
                const range = maxValue - minValue;
                const height =
                  range > 0 ? ((point.value - minValue) / range) * 100 : 50;

                const isSelected = point.date === selectedYear.toString();
                const isCurrent = point.type === "current";
                const isProjected = point.type === "projected";
                const isHistorical = point.type === "historical";

                return (
                  <div
                    key={`${point.date}-${index}`}
                    className="flex-1 flex flex-col items-center justify-end h-full relative group"
                    onClick={() => {
                      const year = parseInt(point.date);
                      if (!isNaN(year)) {
                        setSelectedYear(year);
                      }
                    }}
                  >
                    {/* Connection Line - Only show if we have at least 2 points */}
                    {index < valuationHistory.length - 1 && valuationHistory.length > 1 && (
                      <div
                        className="absolute top-1/2 left-1/2 w-full h-0.5 bg-gradient-to-r from-purple-300 to-pink-300"
                        style={{
                          top: `${100 - height}%`,
                          transform: "translateY(-50%)",
                        }}
                      />
                    )}

                    {/* Data Point */}
                    <div className="relative">
                      <div
                        className={`
                          w-3 h-3 rounded-full transition-all duration-200 cursor-pointer
                          ${isSelected ? "scale-125 ring-4 ring-opacity-30" : ""}
                          ${
                            isCurrent
                              ? "bg-gradient-to-r from-green-500 to-emerald-500"
                              : isProjected
                              ? "bg-gradient-to-r from-purple-500 to-pink-500"
                              : "bg-gradient-to-r from-blue-500 to-cyan-500"
                          }
                          ${
                            isSelected
                              ? isCurrent
                                ? "ring-green-200"
                                : isProjected
                                ? "ring-purple-200"
                                : "ring-blue-200"
                              : ""
                          }
                        `}
                        style={{
                          marginBottom: `${Math.max(
                            0,
                            Math.min(100, height)
                          )}%`,
                        }}
                      />

                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                          <div className="font-semibold">
                            {point.date || "N/A"}
                          </div>
                          <div className="text-green-400">
                            {currency}{" "}
                            {point.value
                              ? (point.value / 1000000).toFixed(1)
                              : "0.0"}
                            M
                          </div>
                          <div className="text-gray-400 text-xs capitalize">
                            {point.type || "unknown"} •{" "}
                            {point.source || "unknown"}
                            {point.confidence && ` • ${point.confidence}% conf.`}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Year Label */}
                    <div
                      className={`
                        mt-2 text-sm font-medium cursor-pointer transition-colors
                        ${
                          isSelected
                            ? "text-gray-900 font-bold"
                            : "text-gray-500 hover:text-gray-700"
                        }
                      `}
                    >
                      {point.date || "N/A"}
                      {isProjected && "*"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Year Details */}
          {currentYearData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {currentYearData.type === "projected" ? "Projected" : "Valuation"}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {currency}{" "}
                      {currentYearData.value
                        ? (currentYearData.value / 1000000).toFixed(1)
                        : "0.0"}
                      M
                    </p>
                  </div>
                  <div
                    className={`p-2 rounded-lg ${
                      currentYearData.type === "projected"
                        ? "bg-purple-100"
                        : currentYearData.type === "current"
                        ? "bg-green-100"
                        : "bg-blue-100"
                    }`}
                  >
                    {currentYearData.type === "projected" ? (
                      <LineChart className="w-6 h-6 text-purple-600" />
                    ) : currentYearData.type === "current" ? (
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    ) : (
                      <Calendar className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      currentYearData.type === "projected"
                        ? "bg-purple-100 text-purple-700"
                        : currentYearData.type === "current"
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {(currentYearData.type || "historical").toUpperCase()}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                    {(currentYearData.source || "unknown").toUpperCase()}
                  </span>
                  {currentYearData.confidence && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                      {currentYearData.confidence}% Confidence
                    </span>
                  )}
                </div>
              </div>

              {/* Growth Metrics - Only show if we have historical data to compare */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {valuationHistory.length > 1 ? "Annual Growth" : "Current Valuation"}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(() => {
                        if (valuationHistory.length > 1) {
                          const currentIndex = valuationHistory.findIndex(
                            (v) => v.date === selectedYear.toString()
                          );
                          if (currentIndex > 0) {
                            const prevPoint = valuationHistory[currentIndex - 1];
                            if (
                              prevPoint &&
                              prevPoint.value > 0 &&
                              currentYearData.value
                            ) {
                              const growth =
                                ((currentYearData.value - prevPoint.value) /
                                  prevPoint.value) *
                                100;
                              return `${growth.toFixed(1)}%`;
                            }
                          }
                        }
                        return "—";
                      })()}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-blue-100">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  {valuationHistory.length > 1 
                    ? "Since previous valuation" 
                    : "Based on current assessment"}
                </div>
              </div>

              {/* Cumulative Growth - Only show if we have multiple years */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {valuationHistory.length > 1 ? "Total Growth" : "Projected Growth"}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(() => {
                        if (valuationHistory.length > 1) {
                          if (valuationHistory.length > 0 && currentYearData.value) {
                            const basePoint = valuationHistory[0];
                            if (basePoint && basePoint.value > 0) {
                              const growth =
                                ((currentYearData.value - basePoint.value) /
                                  basePoint.value) *
                                100;
                              return `${growth.toFixed(1)}%`;
                            }
                          }
                        } else {
                          // If only one data point, show next year projection
                          const projectedPoint = valuationHistory.find(v => v.type === "projected");
                          if (projectedPoint && currentYearData.value) {
                            const growth = ((projectedPoint.value - currentYearData.value) / currentYearData.value) * 100;
                            return `${growth.toFixed(1)}%`;
                          }
                        }
                        return "—";
                      })()}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-green-100">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  {valuationHistory.length > 1 
                    ? `Since ${valuationHistory[0].date}`
                    : "Next 12 months projection"}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
              <span>Historical</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"></div>
              <span>Current</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
              <span>Projected *</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="bg-gray-50 border-t border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {valuationHistory.length > 1 
                ? calculateAnnualGrowth(
                    valuationHistory[0].value,
                    valuationHistory[valuationHistory.length - 1].value,
                    valuationHistory.length - 1
                  ).toFixed(1)
                : "—"}
              %
            </div>
            <div className="text-sm text-gray-600">Avg Annual Growth</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {valuationHistory.length > 1 
                ? (
                    valuationHistory[valuationHistory.length - 1].value /
                    valuationHistory[0].value
                  ).toFixed(1)
                : "—"}
              x
            </div>
            <div className="text-sm text-gray-600">Total Appreciation</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {
                valuationHistory.filter(
                  (v) => v.type === "projected" && v.confidence && v.confidence > 70
                ).length
              }
            </div>
            <div className="text-sm text-gray-600">Projections</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {valuationHistory.length}
            </div>
            <div className="text-sm text-gray-600">Data Points</div>
          </div>
        </div>
        
        {/* Action Buttons */}
        {/* <div className="flex flex-wrap gap-3 justify-center mt-6">
          <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition-opacity">
            <Download className="w-4 h-4" />
            Export Timeline
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default ValuationTimeline;